import CloseIcon from '@mui/icons-material/Close';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, SxProps, Theme, Typography } from '@mui/material';
import { isVideoAsset } from 'common/type-guards';
import { CommonPageForm } from 'modules/pages/pages/page-builder-v2/page-builder-v2.types';
import { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { FieldArrayWithId, UseFieldArrayReturn } from 'react-hook-form';
import { ProductId } from 'store/api/pages-api/pages-api-types';

import { ProductVideo, ProductVideoCreator } from './components';
import { styles } from './product-video-form.styles';

type Props = {
  assetFields: UseFieldArrayReturn<CommonPageForm, `products.${number}.productAssets`>
  currentProductId: ProductId;
  open: boolean;
  onClose: () => void;
  productIdx: number;
};

const ProductVideoForm = ({ assetFields, currentProductId, onClose, open, productIdx }: Props) => {
  const { append, fields, move, remove, update } = assetFields;
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  const onDeleteButton = (
    assetIdx: number,
    asset: FieldArrayWithId<CommonPageForm, `products.${number}.productAssets`>,
  ) => {
    if (asset.type === 'deleted') return;
    if (asset.type === 'added') {
      remove(assetIdx);
    } else {
      update(assetIdx, { ...asset, type: 'deleted' });
    }
  };

  const visibleItems = fields.flatMap((asset, assetIdx) =>
    isVideoAsset(asset) && asset.type !== 'deleted' ? [{ asset, assetIdx }] : [],
  );

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const from = visibleItems[result.source.index].assetIdx;
    const to = visibleItems[result.destination.index].assetIdx;
    if (from !== to) move(from, to);
  };

  if (!enabled) return null;

  return (
    <Box sx={styles.wrapper}>
      <Dialog fullWidth maxWidth="xs" onClose={onClose} open={open}>
        <DialogTitle sx={styles.dialogTitle}>
          Add Video
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <ProductVideoCreator
            currentProductId={currentProductId}
            onAddButton={(data) => {
              append(data, { shouldFocus: false });
              onClose();
            }}
          />
        </DialogContent>
      </Dialog>

      {visibleItems.length > 0 ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable direction="horizontal" droppableId="videos-droppable">
            {(provided) => (
              <Box ref={provided.innerRef} sx={styles.list} {...provided.droppableProps}>
                {visibleItems.map(({ asset, assetIdx }, index) => (
                  <Draggable key={asset.id} draggableId={asset.id} index={index}>
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={provided.draggableProps.style}
                        sx={[styles.cardWrapper, snapshot.isDragging && styles.cardWrapperDragging] as SxProps<Theme>}
                      >
                        <ProductVideo
                          assetIdx={assetIdx}
                          data={asset}
                          onDeleteButton={() => onDeleteButton(assetIdx, asset)}
                          prefix={`products.${productIdx}.productAssets.`}
                        />
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <Box sx={styles.emptyState}>
          <VideoLibraryOutlinedIcon sx={{ fontSize: 36 }} />
          <Typography variant="body2">No videos added yet</Typography>
        </Box>
      )}
    </Box>
  );
};

export { ProductVideoForm };
